import React, { useEffect, useRef, AnimationEvent, useCallback } from 'react';
import Btn, { BtnModifier } from '../../../../../Btn';
import { TooltipPlacement } from '../../../../../../atoms/Tooltip';
import { Icons } from '../../../../../../atoms/SVG';
import { PositionInSelectionInformation, SkipLinkTo } from '../CommonSkipLinks';
import { FormsComboboxMultiSelectListElement } from '../FormsComboboxMultiSelectListElement';
import { FormsComboboxMultiSelectList } from '../FormsComboboxMultiSelectList';
import { FormsComboboxMultiselectSelection } from '../FormsComboboxMultiselectSelection';
import { useSonTranslation } from '../../../../../../../translation/useSonTranslation';

const MODIFIER_HIGHLIGHTING = 'forms-combobox-multiselect__list-item--last-selected';
const ANIMATION_NAME = 'combobox-multiselect-focus-last-selected-item';
const LABEL_ID = 'forms-combobox-multiselect-label';

type SelectedResult = {
  id: string;
  title: string;
  groupState: string;
};

type AnnouncementMultiSelectResultListProps = {
  lastSelectedItem: string; // will look like "id-randomNumber"
  hasSelectedResults: boolean;
  selectedResults: SelectedResult[];
  showReadProtectedInformation: boolean;
  removeParameterByTitle: (result: SelectedResult) => void;
};

export default function AnnouncementMultiSelectResultList({
  lastSelectedItem = '',
  hasSelectedResults,
  selectedResults = [],
  showReadProtectedInformation,
  removeParameterByTitle,
}: AnnouncementMultiSelectResultListProps) {
  const { t } = useSonTranslation('FormsComboBoxMultiSelect');
  const lastSelectedItemRef = useRef<HTMLLIElement>(undefined);
  const propsRef = useRef({
    lastSelectedItem,
    hasSelectedResults,
    selectedResults,
    showReadProtectedInformation,
    removeParameterByTitle,
  });

  const renderHiddenItemInformation = () => {
    return showReadProtectedInformation
      ? (
          <li key="multi-select-hidden-items-info" className="forms-combobox-multiselect__list-item">
            <p className="forms-combobox-multiselect__title">{t('resultlist.hasHiddenItems')}</p>
          </li>
        )
      : null;
  };

  const highlightLastSelectedItem = useCallback(() => {
    if (lastSelectedItemRef.current && propsRef.current.lastSelectedItem !== lastSelectedItem) {
      lastSelectedItemRef.current.classList.add(MODIFIER_HIGHLIGHTING);
    }
  }, [lastSelectedItem]);

  const removeModifierOnAnimationEnd = (event: AnimationEvent<HTMLLIElement>) => {
    if (event.animationName === ANIMATION_NAME && event.target instanceof HTMLLIElement) {
      event.target.classList.remove(MODIFIER_HIGHLIGHTING);
    }
  };

  /**
   * returns the ref if the lastSelectedItem id is equal to the name.
   */
  const setLastSelectedRef = (selectedResult: SelectedResult) => {
    return selectedResult.title === lastSelectedItem.split('-')[0] ? lastSelectedItemRef : null;
  };

  useEffect(() => {
    highlightLastSelectedItem();
    propsRef.current = {
      lastSelectedItem,
      hasSelectedResults,
      selectedResults,
      showReadProtectedInformation,
      removeParameterByTitle,
    };
  }, [
    lastSelectedItem,
    hasSelectedResults,
    selectedResults,
    showReadProtectedInformation,
    removeParameterByTitle,
    highlightLastSelectedItem,
  ]);

  if (hasSelectedResults || showReadProtectedInformation) {
    return (
      <div className="forms-combobox-multiselect__resultlist">
        <div className="forms-combobox-multiselect__label-container">
          <h3 id={LABEL_ID} className="forms-combobox-multiselect__label">
            {t('resultlist.selectionHeadline')}
          </h3>
        </div>
        {hasSelectedResults && selectedResults.length >= 5 && <PositionInSelectionInformation position="top" />}
        {hasSelectedResults && selectedResults.length >= 5 && <SkipLinkTo target="bottom" />}
        {selectedResults.length > 0 && (
          <FormsComboboxMultiSelectList labelId={LABEL_ID}>
            {selectedResults.map((selectedResult, index) => (
              <FormsComboboxMultiSelectListElement
                key={selectedResult.id}
                ref={setLastSelectedRef(selectedResult)}
                onAnimationEnd={removeModifierOnAnimationEnd}
              >
                <FormsComboboxMultiselectSelection
                  id={`recipient-${index}-${selectedResult.id}`}
                  title={selectedResult.title}
                >
                  <input type="hidden" name={`recipient[${index}].id`} value={selectedResult.title} />
                  <Btn
                    modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
                    svgSpriteId={Icons.DELETE_X_SMALL}
                    type="button"
                    text={t('resultlist.deleteButtonText', { itemName: selectedResult.title })}
                    titleText={t('resultlist.deleteButtonTitleText', { itemName: selectedResult.title })}
                    hiddenText
                    tooltipPlacement={TooltipPlacement.LEFT}
                    onClick={() => removeParameterByTitle(selectedResult)}
                  />
                </FormsComboboxMultiselectSelection>
              </FormsComboboxMultiSelectListElement>
            ))}
            {renderHiddenItemInformation()}
          </FormsComboboxMultiSelectList>
        )}
        {hasSelectedResults && selectedResults.length >= 5 && <PositionInSelectionInformation position="bottom" />}
        {hasSelectedResults && selectedResults.length >= 5 && <SkipLinkTo target="top" />}
      </div>
    );
  }
  return null;
}
